<script setup>
import { cookieRef } from '@/@layouts/stores/config'

definePage({
  meta: {
    name: 'incident-kpi-metric',
    subject: 'quality.menu',
    action: 'kpi_metric',
  },
})

import moment from 'moment'
import { useDisplay } from 'vuetify'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'
import KpiMetricCollectDrawer from '@/views/quality/kpi-metric/KpiMetricCollectDrawer.vue'

const route = useRoute()
const router = useRouter()

const { mdAndUp } = useDisplay()

// ─── Route Param ───

const routeType = computed(() => route.params.type ?? 'periodic')
const isPeriodic = computed(() => routeType.value === 'periodic')

// ─── Status Filter ───

const activeStatus = ref('PENDING')

const statusOptions = [
  { title: 'All', value: 'all' },
  { title: 'Pending', value: 'PENDING' },
  { title: 'Completed', value: 'COMPLETED' },
]

// ─── Table State ───

const search = ref('')
const debouncedSearch = refDebounced(search, 500)
const selectedUnit = cookieRef('selectedUnit')
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

// ─── Drawer State ───

const isDrawerOpen = ref(false)
const selectedBatchItem = ref(null)

const openDrawer = item => {
  selectedBatchItem.value = null
  nextTick(() => {
    selectedBatchItem.value = item
    isDrawerOpen.value = true
  })
}

// Reset pagination on status or type change
watch([activeStatus, routeType], () => {
  page.value = 1
})

// ─── Dynamic Headers ───

const headers = computed(() => {
  const cols = [
    { title: 'Title', key: 'kpi_metric.title', sortable: false },
    { title: 'Domain', key: 'kpi_metric.domain.name', sortable: false },
  ]

  if (isPeriodic.value) {
    cols.push({ title: 'Frequency', key: 'kpi_metric.frequency', sortable: false })
  }

  if (activeStatus.value !== 'COMPLETED') {
    cols.push(
      { title: 'Progress', key: 'progress', sortable: false },
      { title: 'Users', key: 'kpi_metric.users', sortable: false },
    )
  }

  if (activeStatus.value === 'COMPLETED') {
    cols.push(
      { title: 'Locations', key: 'collections_count', sortable: false },
      { title: 'Completed At', key: 'completed_at' },
    )
  }

  cols.push(
    { title: 'Status', key: 'status' },
    { title: 'Actions', key: 'actions', sortable: false },
  )

  return cols
})

// ─── Filters ───

const filters = computed(() => {
  const base = {}

  if (isPeriodic.value) {
    base['kpi_metric.frequency'] = 'not-null'
  } else {
    base['kpi_metric.frequency'] = 'null'
  }

  if (activeStatus.value === 'PENDING') {
    base.status = ['PENDING', 'IN_PROGRESS']
  } else if (activeStatus.value === 'COMPLETED') {
    base.status = ['COMPLETED']
  }

  return base
})

// ─── Data Fetching ───

const {
  isFetching,
  data: responseData,
  execute: fetchData,
} = useApi(createUrl('/incident/kpi-metric-batches', {
  query: {
    paged: 1,
    q: debouncedSearch,
    unit_id: selectedUnit,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
    search: filters,
    with: [
      'kpiMetric',
      'kpiMetric.domain',
      'kpiMetric.users',
      'kpiMetric.locations',
      'collections.location',
      'collections.submittedBy',
    ],
  },
}))

const items = computed(() => responseData.value?.data ?? responseData.value ?? [])
const totalItems = computed(() => responseData.value?.meta?.total ?? responseData.value?.length ?? 0)

// ─── Helpers ───

const getProgressPercentage = item => {
  if (!item.collections_count) return 0

  return Math.round((item.submitted_collections_count / item.collections_count) * 100)
}

const getProgressColor = item => {
  const pct = getProgressPercentage(item)
  if (pct === 100) return 'success'
  if (pct >= 50) return 'info'

  return 'warning'
}

const getStatusColor = status => {
  switch (status) {
  case 'PENDING':
    return 'warning'
  case 'IN_PROGRESS':
    return 'info'
  case 'COMPLETED':
    return 'success'
  default:
    return 'secondary'
  }
}
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <!-- Status Filter Bar -->
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <!-- Desktop: Button Toggle -->
        <VBtnToggle
          v-model="activeStatus"
          mandatory
          color="primary"
          variant="tonal"
          density="compact"
          class="d-none d-md-flex"
        >
          <VBtn
            v-for="opt in statusOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.title }}
          </VBtn>
        </VBtnToggle>

        <!-- Mobile: Dropdown -->
        <div class="d-md-none flex-grow-1">
          <AppSelect
            v-model="activeStatus"
            :items="statusOptions"
            item-title="title"
            item-value="value"
            density="compact"
            placeholder="Status"
          />
        </div>

        <VSpacer class="d-none d-md-block" />

        <div class="d-flex align-center gap-4 flex-grow-1 flex-md-grow-0">
          <div
            class="flex-grow-1 flex-md-grow-0"
            style="min-inline-size: 10rem;"
          >
            <AppTextField
              v-model="search"
              placeholder="Search"
              density="compact"
            />
          </div>

          <IconBtn
            variant="tonal"
            class="rounded"
            :disabled="isFetching"
            @click="fetchData"
          >
            <VIcon icon="tabler-refresh" />
          </IconBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- Desktop Table (md and up) -->
      <VDataTableServer
        v-if="mdAndUp"
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items-length="totalItems"
        :items="items"
        :headers="headers"
        fixed-header
        class="text-no-wrap"
        :loading="isFetching"
      >
        <template #item.kpi_metric.title="{ item }">
          {{ item.kpi_metric?.title ?? 'N/A' }}
        </template>

        <template #item.kpi_metric.domain.name="{ item }">
          <VChip
            v-if="item.kpi_metric?.domain?.name"
            size="small"
            color="info"
            variant="tonal"
          >
            {{ item.kpi_metric.domain.name }}
          </VChip>
          <span
            v-else
            class="text-medium-emphasis"
          >N/A</span>
        </template>

        <template #item.kpi_metric.frequency="{ item }">
          <span class="text-capitalize">{{ item.kpi_metric?.frequency ?? 'N/A' }}</span>
        </template>

        <template #item.progress="{ item }">
          <div
            class="d-flex flex-column gap-1"
            style="min-inline-size: 120px;"
          >
            <div class="d-flex align-center justify-space-between">
              <span class="text-body-2 font-weight-medium">
                {{ getProgressPercentage(item) }}%
              </span>
              <span class="text-caption text-medium-emphasis">
                {{ item.submitted_collections_count ?? 0 }} of {{ item.collections_count ?? 0 }}
              </span>
            </div>
            <VProgressLinear
              :model-value="getProgressPercentage(item)"
              color="primary"
              bg-color="primary"
              bg-opacity="0.15"
              height="6"
              rounded
            />
          </div>
        </template>

        <template #item.kpi_metric.users="{ item }">
          <AppAvatarGroup
            v-if="item.kpi_metric?.users?.length > 0"
            :items="item.kpi_metric.users"
            :max="3"
            :size="32"
            name-key="name"
            avatar-key="avatar"
            :default-color="$vuetify.theme.current.dark ? '#4A5072' : '#f6f6f7'"
          />
          <span
            v-else
            class="text-medium-emphasis"
          >N/A</span>
        </template>

        <template #item.collections_count="{ item }">
          <span class="text-body-2">
            {{ item.collections_count ?? 0 }} locations
          </span>
        </template>

        <template #item.completed_at="{ item }">
          {{ item.completed_at ? moment(item.completed_at).format('hh:mm A, Do MMM YYYY') : '&#x2014;' }}
        </template>

        <template #item.status="{ item }">
          <VChip
            size="small"
            :color="getStatusColor(item.status)"
            variant="tonal"
          >
            {{ item.status?.replace('_', ' ') }}
          </VChip>
        </template>

        <template #item.actions="{ item }">
          <VBtn
            v-if="item.status === 'COMPLETED'"
            variant="tonal"
            size="small"
            prepend-icon="tabler-eye"
            @click="openDrawer(item)"
          >
            View
          </VBtn>
          <VBtn
            v-else-if="$can('save', 'quality.kpi_metric_collection')"
            variant="tonal"
            size="small"
            color="primary"
            prepend-icon="tabler-player-play"
            @click="openDrawer(item)"
          >
            Collect
          </VBtn>
        </template>
      </VDataTableServer>

      <!-- Mobile Card Layout (below md) -->
      <template v-if="!mdAndUp">
        <VCardText v-if="isFetching">
          <div class="d-flex flex-column gap-4">
            <VSkeletonLoader
              v-for="n in 3"
              :key="n"
              type="card"
              height="160"
            />
          </div>
        </VCardText>

        <VCardText
          v-else-if="items.length === 0"
          class="text-center text-medium-emphasis pa-8"
        >
          No KPI metrics found.
        </VCardText>

        <VCardText
          v-else
          class="d-flex flex-column gap-4"
        >
          <VCard
            v-for="item in items"
            :key="item.id"
            variant="outlined"
            class="overflow-hidden"
          >
            <!-- Title & Status -->
            <div class="d-flex align-center justify-space-between pa-4 pb-3">
              <span class="text-body-1 font-weight-medium">
                {{ item.kpi_metric?.title ?? 'N/A' }}
              </span>
              <VChip
                size="small"
                :color="getStatusColor(item.status)"
                variant="tonal"
              >
                {{ item.status?.replace('_', ' ') }}
              </VChip>
            </div>

            <!-- Info Items -->
            <div class="d-flex flex-wrap gap-x-4 gap-y-2 px-4 pb-3">
              <div class="d-flex align-center gap-1 text-body-2">
                <VIcon
                  icon="tabler-tag"
                  size="16"
                  class="text-medium-emphasis"
                />
                <VChip
                  v-if="item.kpi_metric?.domain?.name"
                  size="x-small"
                  color="info"
                  variant="tonal"
                >
                  {{ item.kpi_metric.domain.name }}
                </VChip>
                <span
                  v-else
                  class="text-medium-emphasis"
                >N/A</span>
              </div>

              <div
                v-if="isPeriodic"
                class="d-flex align-center gap-1 text-body-2"
              >
                <VIcon
                  icon="tabler-repeat"
                  size="16"
                  class="text-medium-emphasis"
                />
                <span class="text-capitalize">{{ item.kpi_metric?.frequency ?? 'N/A' }}</span>
              </div>

              <VSpacer />

              <div
                v-if="item.status !== 'COMPLETED' && item.kpi_metric?.users?.length"
                class="d-flex align-center gap-1 text-body-2"
              >
                <!--
                  <VIcon
                  icon="tabler-users"
                  size="16"
                  class="text-medium-emphasis"
                  /> 
                -->
                <AppAvatarGroup
                  :items="item.kpi_metric.users"
                  :max="3"
                  :size="28"
                  name-key="name"
                  avatar-key="avatar"
                  :default-color="$vuetify.theme.current.dark ? '#4A5072' : '#f6f6f7'"
                />
              </div>

              <div
                v-if="item.status === 'COMPLETED' && item.completed_at"
                class="d-flex align-center gap-1 text-body-2"
              >
                <VIcon
                  icon="tabler-calendar-check"
                  size="16"
                  class="text-medium-emphasis"
                />
                <span>{{ moment(item.completed_at).format('Do MMM YYYY') }}</span>
              </div>
            </div>

            <!-- Progress -->
            <div
              v-if="item.status !== 'COMPLETED'"
              class="px-4 pb-3"
            >
              <div class="d-flex align-center justify-space-between text-body-2 mb-1">
                <span class="text-medium-emphasis">Progress</span>
                <span class="font-weight-medium">
                  {{ getProgressPercentage(item) }}%
                  <span class="text-medium-emphasis font-weight-regular">
                    ({{ item.submitted_collections_count ?? 0 }}/{{ item.collections_count ?? 0 }})
                  </span>
                </span>
              </div>
              <VProgressLinear
                :model-value="getProgressPercentage(item)"
                color="primary"
                bg-color="primary"
                bg-opacity="0.15"
                height="6"
                rounded
              />
            </div>

            <VDivider />

            <!-- Action -->
            <div class="pa-4">
              <VBtn
                v-if="item.status === 'COMPLETED'"
                variant="tonal"
                prepend-icon="tabler-eye"
                block
                @click="openDrawer(item)"
              >
                View
              </VBtn>
              <VBtn
                v-else-if="$can('save', 'quality.kpi_metric_collection')"
                variant="tonal"
                color="primary"
                prepend-icon="tabler-player-play"
                block
                @click="openDrawer(item)"
              >
                Collect
              </VBtn>
            </div>
          </VCard>
        </VCardText>

        <!-- Mobile Pagination -->
        <VCardText
          v-if="!isFetching && items.length > 0"
          class="d-flex align-center justify-center gap-2"
        >
          <VBtn
            icon="tabler-chevron-left"
            variant="text"
            size="small"
            :disabled="page <= 1"
            @click="page--"
          />
          <span class="text-body-2">
            Page {{ page }} of {{ Math.ceil(totalItems / itemsPerPage) || 1 }}
          </span>
          <VBtn
            icon="tabler-chevron-right"
            variant="text"
            size="small"
            :disabled="page >= Math.ceil(totalItems / itemsPerPage)"
            @click="page++"
          />
        </VCardText>
      </template>
    </VCard>

    <!-- KPI Collect Drawer -->
    <KpiMetricCollectDrawer
      v-model:is-drawer-open="isDrawerOpen"
      :batch-item="selectedBatchItem"
      @collected="fetchData"
    />
  </section>
</template>

<style>
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
